/**
 * @fileoverview Mobile footer accordion (client island).
 * @description Multi-open disclosure for the footer link groups below `lg`,
 * following the WAI-ARIA APG accordion contract via Radix: each group heading
 * is a real heading element whose sole child is a `<button>` carrying
 * `aria-expanded` + `aria-controls`, and collapsed panels are unmounted (out
 * of the tab order). `type="multiple"` lets visitors compare groups.
 * Panels drop Radix's default `role="region"` per APG landmark guidance.
 */

"use client"

import type { ReactNode } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion"

/** One accordion section: heading + pre-rendered panel content. */
export interface FooterAccordionSection {
  id: string
  heading: string
  panel: ReactNode
}

/**
 * Renders the footer link groups as a multi-open accordion (hidden at lg+).
 *
 * @param {Object} props - Component props.
 * @param {readonly FooterAccordionSection[]} props.sections - Group sections.
 * @returns {JSX.Element} Accordion UI.
 */
export function FooterAccordion({
  sections,
}: {
  sections: readonly FooterAccordionSection[]
}): JSX.Element {
  return (
    // The id is load-bearing: Footer.ssr's <noscript> style hides the inert
    // accordion (its panels need hydration) and shows a fully expanded link
    // list instead, so no-JS mobile visitors keep reachable footer links.
    <Accordion id="footer-accordion" type="multiple" className="w-full lg:hidden">
      {sections.map((section) => (
        <AccordionItem
          key={section.id}
          value={section.id}
          className="border-gray-200/80 dark:border-gray-800"
        >
          <AccordionTrigger className="py-3 text-sm font-semibold text-gray-900 hover:no-underline dark:text-gray-50">
            {section.heading}
          </AccordionTrigger>
          <AccordionContent role={undefined}>{section.panel}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
